import { ElementRef, Injectable, Renderer2 } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, fromEvent } from "rxjs";
import { debounceTime } from "rxjs/operators";

export enum WindowBreakpoint {
    None,
    Small, //640
    Medium, //768
    Large, //1024
    ExtraLarge, //1280
    Massive //1536
}

type WindowBreakpointMap = { [width: number]: WindowBreakpoint };

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class ResponsiveService {

    currentBreakpoint = new BehaviorSubject<WindowBreakpoint>(this.getBreakpointFromWidth(window.innerWidth));
    currentBreakpoint$ = this.currentBreakpoint.asObservable();

    currentWindowWidth = new BehaviorSubject<number>(window.innerWidth);
    currentWindowWidth$ = this.currentWindowWidth.asObservable();

    breakpointMap: WindowBreakpointMap;

    constructor() {
        this.subscribeToWindowEvents();
    }

    updateCurrentBreakpoint(): void {
        this.currentBreakpoint.next(this.getBreakpointFromWidth(window.innerWidth));
    }

    shouldDisplayUsingMinWidth(minDisplayWidth: number, widthToCompareTo?: number /* compares to window if not provided */): boolean {

        if (!widthToCompareTo) {
            widthToCompareTo = this.currentWindowWidth.value;
        }

        return minDisplayWidth <= widthToCompareTo;
    }

    shouldDisplayUsingMaxWidth(maxDisplayWidth: number, widthToCompareTo?: number /* compares to window if not provided */): boolean {
        if (!widthToCompareTo) {
            widthToCompareTo = this.currentWindowWidth.value;
        }

        if (maxDisplayWidth >= widthToCompareTo) {
            return true;
        }

        return false;
    }

    shouldDisplayUsingMinBreakpoint(minBreakpoint: WindowBreakpoint, breakpointToCompareTo?: WindowBreakpoint /* compares to window if not provided */): boolean {
        const minBreakpointWidth = this.getWidthFromBreakpoint(minBreakpoint);

        if (!WindowBreakpoint[breakpointToCompareTo]) {
            breakpointToCompareTo = this.getWidthFromBreakpoint(this.currentBreakpoint.value);
        }

        if (minBreakpointWidth <= breakpointToCompareTo) {
            return true;
        }

        return false;
    }

    shouldDisplayUsingMaxBreakpoint(maxBreakpoint: WindowBreakpoint, breakpointToCompareTo?: WindowBreakpoint /* compares to window if not provided */): boolean {
        const maxBreakpointWidth = this.getWidthFromBreakpoint(maxBreakpoint);

        if (!WindowBreakpoint[breakpointToCompareTo]) {
            breakpointToCompareTo = this.getWidthFromBreakpoint(this.currentBreakpoint.value);
        }

        if (maxBreakpointWidth >= breakpointToCompareTo) {
            return true;
        }

        return false;
    }

    getParentWidthOfElementRef(elementRef: ElementRef): number {
        return (elementRef.nativeElement as HTMLElement)?.parentElement?.clientWidth;
    }

    addHiddenElementStyling(elementRef: ElementRef, renderer: Renderer2): void {
        if (!elementRef?.nativeElement) {
            return;
        }

        renderer.setStyle(elementRef.nativeElement, "display", "none");
    }

    removeHiddenElementStyling(elementRef: ElementRef, originalDisplayStyle: string, renderer: Renderer2): void {
        if (!elementRef?.nativeElement) {
            return;
        }

        if (!originalDisplayStyle) {
            renderer.removeStyle(elementRef.nativeElement, "display");
            return;
        }

        renderer.setStyle(elementRef.nativeElement, "display", originalDisplayStyle);
    }

    // ---

    private subscribeToWindowEvents(): void {
        fromEvent(window, "resize")
            .pipe(
                untilDestroyed(this),
                debounceTime(20),
            )
            .subscribe((windowEvent: Event) => {
                const width: number = (windowEvent.target as any).innerWidth;

                this.currentWindowWidth.next(width);
                this.updateCurrentBreakpointUsingWidth(width);
            });
    }

    private updateCurrentBreakpointUsingWidth(width: number): void {
        const newBreakpoint = this.getBreakpointFromWidth(width);

        if (newBreakpoint === this.currentBreakpoint.value) {
            return;
        }

        this.currentBreakpoint.next(newBreakpoint);
    }

    private getBreakpointFromWidth(width: number): WindowBreakpoint {
        const breakpointMap = this.getBreakpointMap();
        let newBreakpoint = WindowBreakpoint.None;

        for (var breakpointWidth of Object.keys(breakpointMap)) {
            if (width >= +breakpointWidth) {
                newBreakpoint = breakpointMap[breakpointWidth];
            }
        }

        return newBreakpoint;
    }

    private getWidthFromBreakpoint(breakpoint: WindowBreakpoint): number {
        const breakpointMap = this.getBreakpointMap();

        for (var breakpointWidth of Object.keys(breakpointMap)) {
            if (breakpoint === breakpointMap[+breakpointWidth]) {
                return +breakpointWidth;
            }
        }

        return null;
    }

    private getBreakpointMap(): WindowBreakpointMap {
        if (!this.breakpointMap) {
            this.breakpointMap = {
                [640]: WindowBreakpoint.Small,
                [768]: WindowBreakpoint.Medium,
                [1024]: WindowBreakpoint.Large,
                [1280]: WindowBreakpoint.ExtraLarge,
                [1536]: WindowBreakpoint.Massive,
            };
        }

        return this.breakpointMap;
    }
}
